import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { galText } from "./accounts";
import * as api from "./api";
import { callTime } from "./calls";
import { useTitle } from "./hooks";
import {
  approvalQuestion,
  cleanTruck,
  countText,
  hiddenStopsText,
  loadDriver,
  loadState,
  measuredScrap,
  keepOrder,
  needsResultFirst,
  parseResult,
  previewChanges,
  type ResultError,
  resultFields,
  resultWarning,
  STATE_TEXT,
  truckError,
} from "./loads";
import { todayIn } from "./pickup";
import { personName } from "./requests";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { driverChoices, memberText } from "./driverRoutes";
import { LOAD_COLUMNS } from "./columns";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

function SessionButton({ saved, auth }: { saved: Saved | null; auth: Auth }) {
  if (!saved || saved.ok || !saved.session) return null;
  return (
    <>
      {" "}
      <button type="button" onClick={() => signOut(auth)}>
        Sign in again
      </button>
    </>
  );
}

function MakeLoadForm({
  org,
  auth,
  user,
  choice,
  onMade,
}: {
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  choice: api.LoadDrivers;
  onMade: (load: api.Load | null) => void;
}) {
  const today = todayIn(org.time_zone);
  const [truck, setTruck] = useState("");
  const [driver, setDriver] = useState("");
  const picked = useRef(false);
  const drivers = driverChoices(choice.drivers ?? []);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = truckError(truck);
    if (problem) {
      setSaved({ ok: false, field: true, text: problem });
      return;
    }
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const entry = { id: entryId.current, day: today, truck: cleanTruck(truck), ...(driver ? { driver_id: driver } : {}) };
      const found = await api.makeLoad(await user.getIdToken(), entry);
      entryId.current = null;
      setTruck("");
      setDriver("");
      picked.current = false;
      setSaved({ ok: true, text: `Made: load ${found.load?.code ?? ""}.` });
      onMade(found.load);
    } catch (e) {
      setSaved(saveFailure(e, "The load"));
    } finally {
      setSaving(false);
    }
  };
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  return (
    <form onSubmit={submit} noValidate data-testid="load-form" aria-labelledby="load-form-title">
      <h2 id="load-form-title">Make a load for today</h2>
      <label htmlFor="load-truck">Truck</label>
      <input
        id="load-truck"
        data-testid="load-truck"
        autoComplete="off"
        placeholder="T7"
        value={truck}
        aria-invalid={Boolean(fieldError) || undefined}
        aria-describedby={fieldError ? "load-truck-error" : undefined}
        onChange={(e) => {
          setTruck(e.target.value);
          if (!picked.current) setDriver(loadDriver(choice, e.target.value));
          entryId.current = null;
          setSaved(null);
        }}
      />
      {fieldError && (
        <p id="load-truck-error" role="alert" className="error" data-testid="load-truck-error">
          {fieldError}
        </p>
      )}
      {drivers.length > 0 && (
        <>
          <label htmlFor="load-driver">Driver</label>
          <select
            id="load-driver"
            data-testid="load-driver"
            value={driver}
            onChange={(e) => {
              setDriver(e.target.value);
              picked.current = true;
              entryId.current = null;
              setSaved(null);
            }}
          >
            <option value="">Me</option>
            {drivers.map((person) => (
              <option key={person.id} value={person.id}>
                {memberText(person)}
              </option>
            ))}
          </select>
        </>
      )}
      <div className="actions">
        <button className="primary" data-testid="save-load" disabled={saving}>
          {saving ? "Saving…" : "Make the load"}
        </button>
      </div>
      <p role="status" data-testid="load-status" className={saved && !saved.ok && !saved.field ? "error" : undefined}>
        {saved && !fieldError && saved.text}
        <SessionButton saved={saved} auth={auth} />
      </p>
    </form>
  );
}

function ResultForm({ load, auth, user, onSaved }: { load: api.Load; auth: Auth; user: User; onSaved: (load: api.Load | null, text: string) => void }) {
  const [raw, setRaw] = useState(() => resultFields(load.result).raw);
  const [pct, setPct] = useState(() => resultFields(load.result).pct);
  const [problem, setProblem] = useState<ResultError | null>(null);
  const [warning, setWarning] = useState("");
  const warned = useRef<string | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const rawField = useRef<HTMLInputElement>(null);
  const pctField = useRef<HTMLInputElement>(null);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setProblem(null);
    setWarning("");
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const found = parseResult({ raw, pct });
    if ("field" in found) {
      setProblem(found);
      (found.field === "raw" ? rawField : pctField).current?.focus();
      return;
    }
    const ask = resultWarning(found.raw_gal, load);
    if (ask && warned.current !== `${found.raw_gal}`) {
      console.info(`loads: the result of load ${load.id} is over the truck or far from the pickups, save again to save it`);
      warned.current = `${found.raw_gal}`;
      setWarning(ask);
      rawField.current?.focus();
      return;
    }
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const entry: api.ResultEntry = { id: entryId.current, ...found, corrects_id: load.result?.id ?? null };
      const next = await api.recordLoadResult(await user.getIdToken(), load.id, entry);
      entryId.current = null;
      warned.current = null;
      setRaw("");
      setPct("");
      onSaved(next.load, `Saved the result of load ${load.code}: ${galText(found.raw_gal)} raw, ${found.scrap_pct}% scrap.`);
    } catch (e) {
      setSaved(saveFailure(e, "The result"));
    } finally {
      setSaving(false);
    }
  };
  const id = (name: string) => `result-${name}-${load.id}`;
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  return (
    <form onSubmit={submit} noValidate className="step-form" data-testid="result-form">
      <h3>{load.result ? "Correct the processor result" : "Record the processor result"}</h3>
      <label htmlFor={id("raw")}>Raw gallons</label>
      <input
        id={id("raw")}
        ref={rawField}
        data-testid="result-raw"
        inputMode="decimal"
        autoComplete="off"
        value={raw}
        aria-invalid={problem?.field === "raw" || undefined}
        aria-describedby={problem?.field === "raw" ? id("raw-error") : warning ? id("raw-warning") : undefined}
        onChange={(e) => edit(() => setRaw(e.target.value))}
      />
      {problem?.field === "raw" && (
        <p id={id("raw-error")} role="alert" className="error" data-testid="result-raw-error">
          {problem.text}
        </p>
      )}
      {warning && (
        <p id={id("raw-warning")} role="alert" className="error" data-testid="result-raw-warning">
          {warning}
        </p>
      )}
      <label htmlFor={id("pct")}>Scrap after processing (%)</label>
      <input
        id={id("pct")}
        ref={pctField}
        data-testid="result-pct"
        inputMode="decimal"
        autoComplete="off"
        value={pct}
        aria-invalid={problem?.field === "pct" || undefined}
        aria-describedby={problem?.field === "pct" ? id("pct-error") : undefined}
        onChange={(e) => edit(() => setPct(e.target.value))}
      />
      {problem?.field === "pct" && (
        <p id={id("pct-error")} role="alert" className="error" data-testid="result-pct-error">
          {problem.text}
        </p>
      )}
      <div className="actions">
        <button data-testid="save-result" disabled={saving}>
          {saving ? "Saving…" : load.result ? "Save the correction" : "Save the result"}
        </button>
      </div>
      {saved && !saved.ok && (
        <p role="alert" className="error">
          {fieldError ?? saved.text}
          <SessionButton saved={saved} auth={auth} />
        </p>
      )}
    </form>
  );
}

function Approval({ load, auth, user, onSaved }: { load: api.Load; auth: Auth; user: User; onSaved: (load: api.Load | null, text: string) => void }) {
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const approvalId = useRef<string | null>(null);
  const openButton = useRef<HTMLButtonElement>(null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmButton.current?.focus();
  }, [confirming]);
  const changes = previewChanges(load);
  const names = new Map(load.stops.map((stop) => [stop.pickup_id, `${stop.account_number} ${stop.account_name}`]));
  const problem = load.preview?.problem;
  const approve = async () => {
    approvalId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const next = await api.approveLoad(await user.getIdToken(), load.id, approvalId.current);
      onSaved(next.load, `Approved: load ${load.code}. ${countText(changes.length, "pickup")} corrected.`);
    } catch (e) {
      setSaved(saveFailure(e, "The approval"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="step-form" data-testid="approval" aria-label={`Approve load ${load.code}`}>
      <h3>Scrap reconciliation</h3>
      {problem ? (
        <p data-testid="approval-problem">{problem}</p>
      ) : (
        <>
          {changes.length ? (
            <ul data-testid="approval-changes">
              {changes.map((change) => (
                <li key={change.pickup_id}>
                  {names.get(change.pickup_id)}: scrap {galText(change.from)} → {galText(change.to)}
                </li>
              ))}
            </ul>
          ) : (
            <p data-testid="approval-none">The scrap of each pickup already matches the result.</p>
          )}
          <p className="hint">An approval writes the corrections and closes the load. It has no undo.</p>
          {confirming ? (
            <div data-testid="approve-load-question">
              <p>{approvalQuestion(load.code, changes.length)}</p>
              <p className="actions">
                <button ref={confirmButton} type="button" className="primary" data-testid="approve-load-confirm" disabled={saving} onClick={approve}>
                  {saving ? "Saving…" : "Approve"}
                </button>
                <button
                  type="button"
                  data-testid="approve-load-cancel"
                  disabled={saving}
                  onClick={() => {
                    setConfirming(false);
                    requestAnimationFrame(() => openButton.current?.focus());
                  }}
                >
                  Cancel
                </button>
              </p>
            </div>
          ) : (
            <div className="actions">
              <button ref={openButton} type="button" data-testid="approve-load" onClick={() => setConfirming(true)}>
                Approve the reconciliation…
              </button>
            </div>
          )}
        </>
      )}
      {saved && !saved.ok && (
        <p role="alert" className="error">
          {saved.text}
          <SessionButton saved={saved} auth={auth} />
        </p>
      )}
    </section>
  );
}

const LOAD_SPAN = LOAD_COLUMNS.length + 1;

function LoadCard({ load, org, auth, user, onChanged }: { load: api.Load; org: api.Bootstrap; auth: Auth; user: User; onChanged: (text: string) => void }) {
  const state = loadState(load);
  const [open, setOpen] = useState(state !== "approved" && load.can_record_result && !load.result);
  const [correcting, setCorrecting] = useState(false);
  const pickups = load.stops.length + load.hidden_stops;
  const saved = (_next: api.Load | null, text: string) => {
    setCorrecting(false);
    onChanged(text);
  };
  return (
    <tbody data-testid="load-row" data-day={load.day} data-truck={load.truck} data-state={state} data-raw={load.raw_gal} className="item">
      <tr>
        <th scope="row" title={`Made ${callTime(load.made_at, org.time_zone)}`}>
          {load.code}
          <span className="sub desk-hide">Made {callTime(load.made_at, org.time_zone)}</span>
        </th>
        <td data-label="Truck">{load.truck}</td>
        <td data-label="Driver">
          <span className="clip short" title={load.mine ? undefined : (load.driver ?? undefined)}>
            {load.mine ? "You" : load.driver ? personName(load.driver) : "–"}
          </span>
        </td>
        <td data-label="State" title={load.approval ? `Approved ${callTime(load.approval.approved_at, org.time_zone)}` : undefined}>
          <span data-testid="load-state">{STATE_TEXT[state]}</span>
          {load.approval && <span className="sub desk-hide">Approved {callTime(load.approval.approved_at, org.time_zone)}</span>}
        </td>
        <td className="num" data-label="Pickups">
          {load.stops.length + load.hidden_stops}
        </td>
        <td className="num" data-label="Raw">
          {galText(load.raw_gal)}
        </td>
        <td className="num" data-label="Scrap">
          {galText(load.scrap_gal)}
        </td>
        <td
          className="num"
          data-label="Processor result"
          data-testid="load-result"
          title={load.result ? `${galText(measuredScrap(load.result))} of ${galText(load.result.raw_gal)}` : undefined}
        >
          {load.result ? (
            <>
              {load.result.scrap_pct}%
              <span className="sub" data-testid="load-result-raw">
                {galText(measuredScrap(load.result))} of {galText(load.result.raw_gal)}
              </span>
            </>
          ) : (
            "–"
          )}
        </td>
        <td data-label="" className="row-actions">
          <button type="button" aria-expanded={open} data-testid="load-details" onClick={() => setOpen(!open)}>
            {open ? "Hide" : "Details"}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="detail-row">
          <td colSpan={LOAD_SPAN} data-label="">
            {load.stops.length > 0 ? (
              <ul className="sub" data-testid="load-stops">
                {load.stops.map((stop) => (
                  <li key={stop.pickup_id}>
                    {stop.account_number} {stop.account_name}: {galText(stop.raw_gal)} raw, {galText(stop.scrap_gal)} scrap
                    {stop.exception && " (exception: keeps its scrap)"}
                  </li>
                ))}
              </ul>
            ) : (
              load.hidden_stops === 0 && <p className="sub">No pickups on this load yet.</p>
            )}
            {load.hidden_stops > 0 && <p className="sub">{hiddenStopsText(load)}</p>}
            {load.result && (
              <p className="sub" data-testid="load-result-detail">
                Processor result: {galText(load.result.raw_gal)} raw, {load.result.scrap_pct}% scrap ({galText(measuredScrap(load.result))}), recorded{" "}
                {callTime(load.result.recorded_at, org.time_zone)}.
              </p>
            )}
            {load.can_record_result &&
              (!load.result || correcting ? (
                <ResultForm load={load} auth={auth} user={user} onSaved={saved} />
              ) : (
                <p className="actions">
                  <button type="button" data-testid="correct-result" onClick={() => setCorrecting(true)}>
                    Correct the result…
                  </button>
                </p>
              ))}
            {load.can_approve && load.result && pickups > 0 && <Approval load={load} auth={auth} user={user} onSaved={saved} />}
          </td>
        </tr>
      )}
    </tbody>
  );
}

function LoadTable({ loads, org, auth, user, onChanged }: { loads: api.Load[]; org: api.Bootstrap; auth: Auth; user: User; onChanged: (text: string) => void }) {
  const [sort, setSort] = useSort("sort", LOAD_COLUMNS);
  const first = useRef<string[] | null>(null);
  const ordered = keepOrder(needsResultFirst(loads), first.current);
  first.current = ordered.map((load) => load.id);
  return (
    <>
      <SortSelect id="load-sort" columns={LOAD_COLUMNS} sort={sort} onSort={setSort} fallback="Newest first" />
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Loads">
        <table className="list loads-table" data-testid="load-list" data-cols="load-sort">
          <thead>
            <tr>
              {LOAD_COLUMNS.map((column) => (
                <SortTh key={column.key} column={column} sort={sort} onSort={setSort} className={column.key === "result" ? "wrap" : undefined} />
              ))}
              <th scope="col">
                <span className="visually-hidden">Details</span>
              </th>
            </tr>
          </thead>
          {(sort ? sortRows(loads, LOAD_COLUMNS, sort) : ordered).map((load) => (
            <LoadCard key={load.id} load={load} org={org} auth={auth} user={user} onChanged={onChanged} />
          ))}
        </table>
      </div>
    </>
  );
}

type Found = ({ loads: api.Load[]; can_make: boolean } & api.LoadDrivers) | "server" | "network" | "session" | undefined;

/** Design 8.8: the truck loads, the processor results and the scrap reconciliation. */
export function LoadList({ org, auth, user, canView }: { org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [found, setFound] = useState<Found>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [note, setNote] = useState("");
  const status = useRef<HTMLParagraphElement>(null);
  useTitle(`Loads · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then((token) => api.loads(token))
      .then((list) => live && setFound(list))
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        if (live) setFound(code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
    return () => {
      live = false;
    };
  }, [user, attempt, canView]);
  const changed = (text: string) => {
    setNote(text);
    setAttempt((n) => n + 1);
    status.current?.focus();
  };
  if (canView === false) {
    return (
      <main data-testid="loads-hidden">
        <h1 tabIndex={-1}>Loads</h1>
        <p>Your role does not show loads.</p>
      </main>
    );
  }
  return (
    <main className="issues-page wide" data-testid="loads-page" data-today={todayIn(org.time_zone)}>
      <h1 tabIndex={-1}>Loads</h1>
      <p ref={status} tabIndex={-1} role="status" data-testid="loads-note">
        {note}
      </p>
      {found === undefined ? (
        <Skeleton shape="table" columns={6} />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : (
        <>
          {found.can_make && <MakeLoadForm org={org} auth={auth} user={user} choice={found} onMade={() => setAttempt((n) => n + 1)} />}
          <section aria-labelledby="loads-title">
            <h2 id="loads-title" data-testid="loads-title">
              Newest loads
            </h2>
            {found.loads.length === 0 ? (
              <p data-testid="loads-none">No loads yet.</p>
            ) : (
              <LoadTable loads={found.loads} org={org} auth={auth} user={user} onChanged={changed} />
            )}
          </section>
        </>
      )}
    </main>
  );
}
